import { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFadeIn } from "@/hooks/useFadeIn";
import {
  GOOGLE_REVIEW_COUNT,
  RATING_PERCENT,
  formatRating,
  reviewCountLabel,
} from "@/lib/googleReviews";

/**
 * As avaliacoes do Google do escritorio — um componente, quatro paginas.
 *
 * Substituiu o TestimonialsSection em 20/10/2026. Antes disso a home e as duas
 * LPs antigas traziam o widget real do Google (Elfsight) sem nota e sem
 * ressalva, e a LP de Queens trazia tres depoimentos estaticos com cinco
 * estrelas cheias e nenhum widget. Ou seja: a pagina que dizia menos era a
 * que afirmava mais.
 *
 * Tres coisas que o componente garante em qualquer pagina onde entrar:
 *
 * 1. A NOTA REAL. 4,8 com a quinta estrela pintada em 96%, nao cinco cheias.
 *    O numero vem de @/lib/googleReviews, que agora e a unica fonte.
 *
 * 2. A RESSALVA DA NY RPC 7.1. Avaliacao de cliente descreve resultado, e
 *    alegacao de resultado precisa vir acompanhada de que resultado anterior
 *    nao garante resultado semelhante. Ela nao existia em lugar nenhum do site
 *    ate a LP de Queens, e o widget da home mostra exatamente o mesmo tipo de
 *    conteudo. Nao e opcional e nao depende de prop.
 *
 * 3. UM CAMINHO QUANDO O WIDGET NAO PINTA. O Elfsight e script de terceiro: se
 *    a rede falhar, se o dominio estiver bloqueado ou se o webview do app
 *    engolir o embed — e 84% do trafego dessas LPs e webview — o que sobra e um
 *    buraco de 430px onde deveria estar a prova social, numa pagina paga cujo
 *    unico trabalho e levar ao WhatsApp. Depois de PAINT_GRACE_MS sem pintar,
 *    entram as tres citacoes do i18n no lugar. Se o widget pintar depois
 *    disso, o MutationObserver continua de pe e as citacoes saem.
 *
 * O script so e injetado quando a secao se aproxima da viewport, entao nada
 * aqui entra no caminho critico nem mexe no FCP.
 */

const ELFSIGHT_SRC = "https://static.elfsight.com/platform/platform.js";
const ELFSIGHT_APP_CLASS = "elfsight-app-cf3266d7-a8f0-4264-9f66-3f2ab3710446";

/** Quanto esperar o widget pintar antes de mostrar as citacoes no lugar. */
const PAINT_GRACE_MS = 4000;

type Stage = "idle" | "loading" | "painted" | "failed";

const STAR_SIZES = { xs: "h-3.5 w-3.5", sm: "h-4 w-4", md: "h-5 w-5" } as const;

type StarSize = keyof typeof STAR_SIZES;

const StarRow = ({
  filled,
  size,
  starClass,
}: {
  filled: boolean;
  size: StarSize;
  starClass: string;
}) => (
  <span className="flex w-max items-center">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        className={`${STAR_SIZES[size]} shrink-0 ${filled ? starClass : "fill-transparent text-gold/35"}`}
      />
    ))}
  </span>
);

/**
 * Fileira de estrelas pintada proporcionalmente a nota: duas camadas, a de
 * cima recortada em RATING_PERCENT. E o unico jeito de mostrar 4,8 sem
 * arredondar para 5 nem para 4 — e cinco estrelas cheias para uma casa 4,8
 * afirma meio ponto que o perfil nao tem.
 *
 * starClass existe porque as paginas de localidade usam o dourado #D4AF37 em
 * vez do token --gold, que e mais claro. Mudar a cor delas nao e assunto
 * deste commit.
 */
export const RatingStars = ({
  size = "sm",
  starClass = "fill-gold text-gold",
}: {
  size?: StarSize;
  starClass?: string;
}) => (
  <span className="relative inline-flex" aria-hidden="true">
    <StarRow filled={false} size={size} starClass={starClass} />
    <span
      className="absolute inset-y-0 left-0 flex items-center overflow-hidden"
      style={{ width: `${RATING_PERCENT}%` }}
    >
      <StarRow filled size={size} starClass={starClass} />
    </span>
  </span>
);

type RatingLineProps = {
  onDark?: boolean;
  size?: StarSize;
  /** "stacked" e para coluna estreita: estrelas em cima, nota e quantidade embaixo. */
  layout?: "row" | "stacked";
  className?: string;
};

/** Nota + quantidade. Usada nos heros das LPs e no cabecalho da faixa. */
export const GoogleRatingLine = ({
  onDark = false,
  size = "sm",
  layout = "row",
  className = "",
}: RatingLineProps) => {
  const { t, language } = useLanguage();
  const muted = onDark ? "text-primary-foreground/70" : "text-navy/70";
  const strong = onDark ? "text-primary-foreground" : "text-navy";

  const rating = (
    <span className={`font-semibold ${strong}`}>
      {formatRating(language)}
      <span className="sr-only"> {t("reviews.outOf5")}</span>
    </span>
  );

  if (layout === "stacked") {
    return (
      <div className={`flex flex-col items-end gap-1 text-xs ${muted} ${className}`}>
        <RatingStars size={size} />
        <p>
          {rating} · {reviewCountLabel(t)}
        </p>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 text-sm ${muted} ${className}`}>
      <RatingStars size={size} />
      {rating}
      <span>{reviewCountLabel(t)}</span>
    </div>
  );
};

/**
 * As citacoes de reserva. Vem do i18n do site (testimonials.*), nao copiadas
 * para dentro deste arquivo: depoimento de cliente nao pode existir em duas
 * versoes que divergem quando uma for editada.
 */
const FallbackQuotes = () => {
  const { t } = useLanguage();

  return (
    <div className="grid gap-10 md:grid-cols-3">
      {[1, 2, 3].map((n) => (
        <figure key={n} className="border-l-2 border-gold pl-5">
          <blockquote className="font-heading text-lg leading-relaxed text-foreground/90">
            {t(`testimonials.${n}.text`)}
          </blockquote>
          <figcaption className="mt-4 text-sm">
            <span className="font-semibold text-foreground">{t(`testimonials.${n}.name`)}</span>
            <span className="block text-muted-foreground">{t(`testimonials.${n}.location`)}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
};

type GoogleReviewsProps = {
  /**
   * "page" e a faixa da home: cabecalho centralizado com eyebrow e subtitulo.
   * "campaign" e a das LPs pagas: titulo a esquerda, nota a direita, sem
   * animacao de entrada — em pagina de anuncio o conteudo aparece de uma vez.
   */
  variant?: "page" | "campaign";
  /** Titulo ja traduzido. Tem precedencia sobre titleKey. */
  title?: string;
  titleKey?: string;
  /** Subtitulo ja traduzido, usado no variant campaign. */
  subtitle?: string;
};

const GoogleReviews = ({
  variant = "page",
  title,
  titleKey = "testimonials.title",
  subtitle,
}: GoogleReviewsProps) => {
  const { t } = useLanguage();
  const campaign = variant === "campaign";
  const { ref: headerRef, isVisible: headerVisible } = useFadeIn();
  const sectionRef = useRef<HTMLElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<Stage>("idle");

  // Só começa a carregar quando a seção se aproxima da viewport.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setStage((s) => (s === "idle" ? "loading" : s));
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStage((s) => (s === "idle" ? "loading" : s));
          observer.disconnect();
        }
      },
      // 200px de folga: começa a baixar um pouco antes de entrar em cena, para
      // a pessoa não chegar na seção e encontrar o espaço reservado vazio.
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Injeta o script e vigia se o widget realmente pintou.
  useEffect(() => {
    if (stage === "idle" || stage === "painted") return;

    let alive = true;
    const host = hostRef.current;
    const painted = () => Boolean(host?.querySelector(`.${ELFSIGHT_APP_CLASS}`)?.childElementCount);

    if (painted()) {
      setStage("painted");
      return;
    }

    let observer: MutationObserver | undefined;
    if (host && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        if (alive && painted()) setStage("painted");
      });
      observer.observe(host, { childList: true, subtree: true });
    }

    // O prazo só corre na primeira tentativa; depois de "failed" o observer
    // continua de pé sozinho, para um widget lento ainda poder aparecer.
    const timer =
      stage === "loading"
        ? window.setTimeout(() => {
            if (alive) setStage("failed");
          }, PAINT_GRACE_MS)
        : undefined;

    if (!document.querySelector(`script[src="${ELFSIGHT_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = ELFSIGHT_SRC;
      script.async = true;
      script.addEventListener("error", () => {
        if (alive) setStage("failed");
      });
      document.body.appendChild(script);
    }

    return () => {
      alive = false;
      observer?.disconnect();
      if (timer) window.clearTimeout(timer);
    };
  }, [stage]);

  const loading = stage === "loading";
  const showFallback = stage === "failed";

  return (
    <section
      ref={sectionRef}
      className={campaign ? "bg-background py-16 md:py-24" : "bg-background py-24 md:py-32"}
    >
      {/* Sem botão de escrever avaliação e sem resumo de IA em página paga. */}
      {campaign && (
        <style>{`
          .arandia-google-reviews .es-header-write-review-button-container,
          .arandia-google-reviews .swiper-slide:has(.es-ai-summary-author-name),
          .arandia-google-reviews .es-carousel-item:has(.es-ai-summary-author-name) {
            display: none !important;
          }
        `}</style>
      )}

      <div className="container-custom">
        {campaign ? (
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6 md:mb-12">
            <div>
              <h2 className="max-w-xl font-heading text-3xl font-bold leading-tight text-foreground md:text-4xl">
                {title ?? t(titleKey)}
              </h2>
              {subtitle && <p className="mt-3 max-w-md text-muted-foreground">{subtitle}</p>}
            </div>
            <GoogleRatingLine size="md" />
          </div>
        ) : (
          <div
            ref={headerRef}
            className={`text-center max-w-2xl mx-auto mb-16 fade-up ${headerVisible ? "visible" : ""}`}
          >
            <span className="eyebrow-text">{t("testimonials.label")}</span>
            <h2 className="font-heading font-bold text-foreground text-3xl md:text-4xl lg:text-5xl mt-3 mb-4">
              {title ?? t(titleKey)}
            </h2>
            {/*
              A home não repete a nota aqui: o cabeçalho é editorial e o widget
              do Google vem logo abaixo com a avaliação dentro dele. Nas LPs a
              linha de nota existe porque é ela que precisa sobreviver se o
              widget não pintar.
            */}
            <p className="text-muted-foreground">{t("testimonials.subtitle")}</p>
          </div>
        )}

        {/* Widget real do Google. Fica montado mesmo depois de "failed". */}
        <div
          ref={hostRef}
          className={loading ? (campaign ? "min-h-[430px]" : "min-h-[520px] md:min-h-[430px]") : ""}
          aria-busy={loading}
        >
          {stage !== "idle" && (
            <div className={`arandia-google-reviews ${showFallback ? "hidden" : ""}`}>
              <div className={ELFSIGHT_APP_CLASS} data-elfsight-app-lazy />
            </div>
          )}
        </div>

        {showFallback && <FallbackQuotes />}

        {/* NY RPC 7.1 — ver o comentário no topo do arquivo. */}
        <p className="mt-12 border-t border-border pt-5 text-xs leading-relaxed text-muted-foreground">
          {t("reviews.disclaimer")}
        </p>
      </div>
    </section>
  );
};

export default GoogleReviews;
